import { ChangeDetectionStrategy, Component, computed, input, output, signal, ViewEncapsulation } from "@angular/core";
import { Doc } from "./s25.document.const";
import { ReportBuilderCell } from "./s25.document.report.builder.table.component";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { DocumentService } from "../../../services/document.service";
import { DocumentUtil } from "./s25.document.util";
import { Bind } from "../../../decorators/bind.decorator";
import { S25Util } from "../../../util/s25-util";
import { CssProperty } from "../../s25-styles/s25.css.rule.component";

@Component({
    standalone: false,
    selector: "s25-ng-document-report-builder-cell-editor",
    template: `
        <div>
            <label>Label</label>
            <input
                class="c-input label-input"
                [class.invalid]="!!errors.label"
                type="text"
                [(ngModel)]="cell().label"
                (ngModelChange)="updateDisplay(cell())"
            />
            @if (errors.label) {
                <span class="error ngRed">{{ errors.label }}</span>
            }
        </div>
        <div>
            <label>Width</label>
            <input
                #widthInput
                class="c-input width-input"
                type="number"
                min="0"
                max="100"
                [ngModel]="cell().width() | number: '1.1-1'"
                (ngModelChange)="onCellWidthChange(widthInput, $event)"
                (keydown.enter)="widthChange.emit(widthInput.valueAsNumber)"
                (blur)="widthChange.emit(widthInput.valueAsNumber)"
            />
        </div>
        <div>
            <label>Type</label>
            <select [(ngModel)]="cell().variable().type" (ngModelChange)="onVariableTypeChange()">
                <option [value]="'variable'">Variable</option>
                <option [value]="'function'">Function</option>
                <option [value]="'subreport'">Subreport</option>
            </select>
        </div>
        <div>
            <label>Data</label>
            <s25-ng-bpe-variable
                [state]="cell().variable().state"
                [type]="templateVariableKey()"
                [codeMode]="false"
                [documentType]="documentType()"
                [hasPost]="false"
                [hasFormatExample]="false"
                [prefix]="false"
                [wrap]="false"
                [hasNoneItem]="true"
                [itemFilter]="addSubreports"
                [placeholder]="templateVariablePlaceholder()"
                [scrollIntoView]="false"
                (variableChange)="onVariableChange(cell())"
            />
            @if (cell().variable().type() === "function" && !!cell().variable().state.var) {
                <s25-ng-document-report-builder-cell-editor-function
                    [(arguments)]="cell().variable().arguments"
                    [type]="$any(cell().variable().state.var)"
                    [documentType]="documentType()"
                    (changed)="updateDisplay(cell())"
                />
            }
        </div>
        <div>
            <label>Styles</label>
            <s25-ng-css-rule
                [(styles)]="cell().css"
                [allowedProperties]="Doc.supportedCssProperties"
                [validateProperty]="validateCssProperty"
                [(valid)]="cssValid"
                (validChange)="validate()"
                (stylesChange)="changed.emit()"
            />
        </div>
    `,
    styles: `
        :host {
            --border-width: 1px;
            --border-color: #0002;
            --border-radius: 3px;
            --resize-handle-width: 5px;
            --control-bg: #f2f2f2;
            --green: #60a725;
            --red: #d62000;
            --red-alpha: color-mix(in lch, var(--red), #fff0 85%);
            --cell-highlight: color-mix(in lch, var(--cn-blue), #fff0 85%);
        }

        ::ng-deep body.nm-party--on s25-ng-document-report-builder-cell-editor {
        }

        :host {
            display: grid;
            gap: 0.5rem;
            grid-template-columns: 100%;
        }

        label {
            font-weight: bold;
        }

        :host > div {
            display: grid;
            gap: 0.1rem;
            grid-template-columns: 100%;
        }

        select {
            align-items: center;
            background-color: var(--cn-blue);
            border-radius: 3px;
            color: white !important;
            border: 0;
            padding-block: 0.2em;
        }

        ::ng-deep ngb-popover-window:has(s25-ng-document-report-builder-cell-editor) {
            --bs-popover-max-width: 350px !important;
            width: 350px;
        }

        ::ng-deep s25-ng-document-report-builder-cell-editor s25-ng-bpe-variable .selectedVarLabel {
            display: none;
        }

        ::ng-deep s25-ng-document-report-builder-cell-editor s25-dropdown-paginated .select2-choice {
            padding-left: 0 !important;
        }

        .invalid.invalid {
            border-color: var(--conflict-color) !important;
            background: color-mix(in hsl, var(--conflict-color), transparent 90%);
        }

        .error {
            font-size: 0.9em;
            display: block;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentReportBuilderCellEditorComponent {
    documentType = input.required<Doc.ReportBuilderTypeId>();
    cell = input.required<ReportBuilderCell>();

    changed = output<void>();
    widthChange = output<number>();
    errors: { label?: string } = {};
    cssValid = signal(true);

    protected readonly Doc = Doc;

    templateVariableKey = computed(() => {
        const type = this.cell().variable().type();
        if (type === "variable") return this.documentType();
        if (type === "function") return "reportBuilderFunctions";
        if (type === "subreport") return "none";
    });
    templateVariablePlaceholder = computed(() => S25Util.firstCharToUpper(this.cell().variable().type()));

    @Bind
    async addSubreports(items: DropDownItem[]) {
        if (this.cell().variable().type() !== "subreport") return items;
        if (Doc.type[this.documentType()].type === "subreport") return items; // No nested subreports

        const subreports = await DocumentService.getDocumentList(null, "subreport");
        const mapped = subreports.map((subreport) => ({
            val: `subreport.${subreport.itemId}`,
            txt: subreport.itemName,
            grp: "Subreports",
        }));
        return [{ isGroup: true, groupName: "Subreports" }, ...mapped, ...items];
    }

    onVariableTypeChange() {
        this.cell().variable().state.variable = undefined;
        this.cell().variable().arguments.set([]);
    }

    onVariableChange(cell: ReportBuilderCell) {
        if (cell.variable().type() === "subreport" && cell.variable().state.variable) {
            cell.variable().subreportId = Number(cell.variable().state.variable.val.split(".")[1]);
        }
        this.cell().variable().arguments.set([]);
        this.updateDisplay(cell);
    }

    updateDisplay(cell: ReportBuilderCell) {
        const value = DocumentUtil.getReportBuilderCellDisplayValue(cell);
        cell.display.set(value);
        this.onChange();
    }

    onChange() {
        this.validate();
        this.changed.emit();
    }

    // We update widths on enter and blur, but if a user clicks the input's native arrow controls the element never
    // gets focused, so it won't update on blur. So, we need to update widths if there is a change to the value when
    // the element is not focused.
    onCellWidthChange(input: HTMLElement, width: number) {
        if (document.activeElement === input) return;
        this.widthChange.emit(width);
    }

    validate() {
        this.cell().valid.set(true);
        this.errors = {};

        if (/[\^]/.test(this.cell().label())) {
            this.errors.label = "Label cannot contain ^";
            this.cell().valid.set(false);
        }

        if (this.cell().variable().type() === "function" && !!this.cell().variable().state.variable) {
            const args = this.cell().variable().arguments();
            const func = Doc.reportBuilderFunctions[this.cell().variable().state.variable.val];
            for (const arg of args) {
                arg.error.set(undefined);
                if (arg.type === "text" && /[\^,]/.test(arg.text)) {
                    arg.error.set("Function text cannot contain: ,^");
                }
                if (arg.error()) this.cell().valid.set(false);
            }
            if (func.fixedArguments) {
                // Check that all fixed arguments are filled out
                for (let i = 0; i < func.fixedArguments.length; i++) {
                    const arg = args[i];
                    const spec = func.fixedArguments[i];
                    if (arg && !spec.optional && DocumentUtil.isReportBuilderFunctionArgumentEmpty(arg)) {
                        arg.error.set(`Please select a value`);
                        this.cell().valid.set(false);
                    }
                }
            }
        }

        if (!this.cssValid()) this.cell().valid.set(false);
    }

    validateCssProperty(property: CssProperty) {
        if (!!property.property && /[\^]/.test(property.value)) return "Styles cannot contain ^";
    }
}
